import { useMutation } from "@tanstack/react-query";

import type { ActivationContractDigestsActivationDigestGranularity } from "@similarweb/pro-api-client";
import Sentry from "@similarweb/sentry-logger";

import { apiClient } from "app/modules/api-client";
import {
    UPDATE_SUBSCRIPTION_TEXT,
    SUBSCRIBE_TOAST_SUCCESS_TEXT,
} from "app/pages/activation/components/AlertsSubscription/texts";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";

import { useAlertsRelatedQueriesInvalidation } from "./useAlertsRelatedQueriesInvalidation";

const SUBSCRIBE_TO_ALERTS_MUTATION_KEY = "subscribe-to-alerts";

export type SubscribeToAlertsMutationPayload = {
    granularity: ActivationContractDigestsActivationDigestGranularity;
};

export const useSubscribeToAlertsMutation = (activationSetId: string, isSubscribed = false) => {
    const invalidateAlertsRelatedQueries = useAlertsRelatedQueriesInvalidation();
    const { showErrorToast, showSuccessToast } = useToasts();
    const email = swSettings.user?.username;

    return useMutation({
        mutationKey: [SUBSCRIBE_TO_ALERTS_MUTATION_KEY],
        mutationFn: ({ granularity }: SubscribeToAlertsMutationPayload) =>
            apiClient.Alerts.userdataActivationSubscriptionsAlertsUpdate(activationSetId, { granularity }),
        onSuccess: async () => {
            await invalidateAlertsRelatedQueries(activationSetId);

            const toastText = isSubscribed ? UPDATE_SUBSCRIPTION_TEXT : SUBSCRIBE_TOAST_SUCCESS_TEXT;

            showSuccessToast(translate(toastText, { email }));
        },
        onError: (error: Error) => {
            Sentry.errorTracker({
                e: error,
                message: "Error in Alerts.userdataActivationSubscriptionsAlertsUpdate request",
                level: "error",
                params: {
                    extra: {
                        activationSetId,
                    },
                },
            });

            showErrorToast(translate("common.api.error", { error: error?.message }));
        },
    });
};
